"use client";

import { Node, mergeAttributes } from "@tiptap/core";
import {
    NodeViewContent,
    NodeViewWrapper,
    ReactNodeViewRenderer,
    type ReactNodeViewProps,
} from "@tiptap/react";
import { scaledPx } from "../../lib/utils";
import { BlockSettingsButton } from "../blockControls";
import { BlockItemsReorder } from "../blockItemsReorder";
import { BlockTypeSwitcher } from "../blockTypeSwitcher";
import { SCENE_BLOCK_REGISTRY } from "../registry";
import { useMetadata } from "@/features/lomdaEditorV2/store";

const D = SCENE_BLOCK_REGISTRY.numberedSteps;

function getNumberedStepsDefaultItem(language: string) {
    const isHe = language === "he";
    return {
        type: D.itemNodeName,
        content: [
            {
                type: D.titleField.nodeName,
                attrs: { textAlign: "start" },
                content: [{ type: "text", text: isHe ? "כותרת" : "Title" }],
            },
            {
                type: D.bodyField.nodeName,
                attrs: { textAlign: "start" },
                content: [
                    {
                        type: "text",
                        text: isHe ? "תיאור לדוגמה" : "Example description",
                    },
                ],
            },
        ],
    };
}

function getEmptyNumberedStepsItem() {
    return {
        type: D.itemNodeName,
        content: [
            { type: D.titleField.nodeName, attrs: { textAlign: "start" } },
            { type: D.bodyField.nodeName, attrs: { textAlign: "start" } },
        ],
    };
}

export function buildDefaultNumberedSteps(
    count: number = D.itemCount.default,
    language: string = "en"
) {
    const item = getNumberedStepsDefaultItem(language);
    return {
        type: D.nodeName,
        content: Array.from({ length: count }, () => item),
    };
}

function NumberedStepsView({
    node,
    editor,
    getPos,
}: ReactNodeViewProps) {
    const metadata = useMetadata();
    const language = metadata?.language ?? "en";

    const insertItem = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        const end = pos + node.nodeSize - 1;
        editor
            .chain()
            .focus()
            .insertContentAt(end, getNumberedStepsDefaultItem(language))
            .run();
    };

    const deleteBlock = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run();
    };

    // Delete the step at `index`. Removing the last one drops the whole block —
    // an empty numberedSteps container (content is `item+`) would be invalid.
    const deleteItemAt = (index: number) => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        if (node.childCount <= 1) {
            deleteBlock();
            return;
        }
        let from = pos + 1;
        for (let i = 0; i < index; i++) from += node.child(i).nodeSize;
        const to = from + node.child(index).nodeSize;
        editor.chain().focus().deleteRange({ from, to }).run();
    };

    return (
        <NodeViewWrapper
            as="div"
            data-scene-block={D.type}
            className="group/scene-block relative w-full"
        >
            <BlockSettingsButton
                customSection={
                    <div className="flex flex-col gap-3">
                        <BlockTypeSwitcher
                            editor={editor}
                            getPos={getPos}
                            node={node}
                        />
                        <BlockItemsReorder
                            editor={editor}
                            getPos={getPos}
                            node={node}
                            getItemMeta={(item) => ({
                                label:
                                    item.firstChild?.textContent.trim() ?? "",
                            })}
                            onDeleteItem={deleteItemAt}
                        />
                    </div>
                }
                onAddItem={insertItem}
                addItemLabelKey="lomda.scene-block.add-step"
                onDelete={deleteBlock}
            />
            <NodeViewContent<typeof D.blockTag>
                as={D.blockTag}
                className="w-full"
                style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    counterReset: "scene-step",
                }}
            />
        </NodeViewWrapper>
    );
}

function NumberedStepsItemView(_: ReactNodeViewProps) {
    return (
        <NodeViewWrapper
            as={D.itemTag}
            className="relative flex flex-row items-start"
            style={
                {
                    gap: scaledPx(32),
                    counterIncrement: "scene-step",
                } as React.CSSProperties
            }
        >
            <span
                contentEditable={false}
                className="shrink-0 flex items-center justify-center rounded-full"
                style={{
                    width: "2em",
                    height: "2em",
                    backgroundColor:
                        "color-mix(in srgb, currentColor 14%, transparent)",
                    fontSize: scaledPx(32),
                    fontWeight: 700,
                    marginTop: scaledPx(-7),
                }}
                data-scene-step-number
            />
            <NodeViewContent as="div" className="flex-1 min-w-0" />
        </NodeViewWrapper>
    );
}

export const NumberedSteps = Node.create({
    name: D.nodeName,
    group: "block",
    content: `${D.itemNodeName}+`,
    defining: true,
    isolating: true,

    parseHTML() {
        return [
            {
                tag: `${D.blockTag}[data-scene-block=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.blockTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(NumberedStepsView);
    },
});

export const NumberedStepsItem = Node.create({
    name: D.itemNodeName,
    group: D.itemNodeName,
    content: `${D.titleField.nodeName} ${D.bodyField.nodeName}`,
    defining: true,
    isolating: true,

    parseHTML() {
        return [
            {
                tag: `${D.itemTag}[data-scene-block-item=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.itemTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-item": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(NumberedStepsItemView);
    },

    addKeyboardShortcuts() {
        return {
            // Enter inside the body adds a new step right after the current one,
            // landing the cursor in the new title so the user can start typing.
            Enter: ({ editor }) => {
                const { $from } = editor.state.selection;
                for (let depth = $from.depth; depth > 0; depth--) {
                    const node = $from.node(depth);
                    if (node.type.name !== D.bodyField.nodeName) continue;

                    const itemDepth = depth - 1;
                    const itemNode = $from.node(itemDepth);
                    if (itemNode.type.name !== D.itemNodeName) return false;

                    const itemPos = $from.before(itemDepth);
                    const itemEnd = itemPos + itemNode.nodeSize;
                    // +2 = into the new item, into the new title
                    const focusPos = itemEnd + 2;

                    editor
                        .chain()
                        .insertContentAt(itemEnd, getEmptyNumberedStepsItem())
                        .setTextSelection(focusPos)
                        .focus()
                        .run();
                    return true;
                }
                return false;
            },
            // Backspace on a fully empty step removes it. If it was the last
            // remaining step, drop the whole numberedSteps block too — leaving
            // an empty container would just be visual noise.
            Backspace: ({ editor }) => {
                const { $from, empty } = editor.state.selection;
                if (!empty) return false;

                for (let depth = $from.depth; depth > 0; depth--) {
                    const node = $from.node(depth);
                    if (node.type.name !== D.itemNodeName) continue;

                    const title = node.child(0);
                    const body = node.child(1);
                    if (title.content.size > 0 || body.content.size > 0)
                        return false;

                    const parent = $from.node(depth - 1);
                    const isLastItem = parent.childCount <= 1;
                    const from = isLastItem
                        ? $from.before(depth - 1)
                        : $from.before(depth);
                    const to = isLastItem
                        ? from + parent.nodeSize
                        : from + node.nodeSize;
                    editor.chain().deleteRange({ from, to }).run();
                    return true;
                }
                return false;
            },
        };
    },
});

export const NumberedStepsTitle = Node.create({
    name: D.titleField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.titleField.htmlTag}[data-scene-block-field=${D.titleField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.titleField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.titleField.nodeName,
            }),
            0,
        ];
    },
});

export const NumberedStepsBody = Node.create({
    name: D.bodyField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.bodyField.htmlTag}[data-scene-block-field=${D.bodyField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.bodyField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.bodyField.nodeName,
            }),
            0,
        ];
    },
});

export const numberedStepsExtensions = [
    NumberedSteps,
    NumberedStepsItem,
    NumberedStepsTitle,
    NumberedStepsBody,
];
